import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { format } from "date-fns";
import { GameweekAssignmentPanel } from "../components/gameweeks";
import { get, post, delWithBody } from "../utils/networkUtils";

const GameweekBuilderPage = () => {
  const { id: tournamentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlGameweekId = searchParams.get("gameweek");

  const [tournament, setTournament] = useState(null);
  const [gameweeks, setGameweeks] = useState([]);
  const [selectedGameweekId, setSelectedGameweekId] = useState(
    urlGameweekId || ""
  );
  const [selectedGameweek, setSelectedGameweek] = useState(null);
  const [assignedMatches, setAssignedMatches] = useState([]);
  const [availableMatches, setAvailableMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [dateFilter, setDateFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchGameweeks = useCallback(async () => {
    try {
      const response = await get(
        `/admin/gameweek?tournamentCalendarId=${tournamentId}&limit=100`
      );

      if (response?.data?.gameweeks) {
        const mappedGameweeks = response.data.gameweeks.map((gw) => ({
          id: gw.id,
          number: gw.number,
          name: gw.name,
          assignedMatches: gw.assignedMatches || 0,
          status: gw.status,
          startsAt: gw.startsAt,
          endsAt: gw.endsAt,
          deadlineAt: gw.deadlineAt,
        }));
        setGameweeks(mappedGameweeks);
        return mappedGameweeks;
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch gameweeks:", error);
      setSnackbar({
        open: true,
        message: "Failed to load gameweeks",
        severity: "error",
      });
      return [];
    }
  }, [tournamentId]);

  const fetchAvailableMatches = useCallback(async (gameweekId) => {
    if (!gameweekId) return;

    try {
      const response = await get(
        `/admin/gameweek/${gameweekId}/available-matches`
      );

      if (response?.data?.matches) {
        const mapped = response.data.matches.map((m) => ({
          id: m.id,
          optaFixtureId: m.optaFixtureId,
          kickoffAt: m.kickoffAt,
          gameweek: m.gameweek,
          status: m.status,
          homeScore: m.homeScore,
          awayScore: m.awayScore,
          homeContestantId: m.homeContestantId,
          awayContestantId: m.awayContestantId,
          homeContestantName: m.homeContestantName || 'TBD',
          awayContestantName: m.awayContestantName || 'TBD',
          tournamentName: m.tournamentName || null,
          sourceTournamentCalendarId: m.sourceTournamentCalendarId || null,
        }));

        setAvailableMatches(mapped);
      } else {
        setAvailableMatches([]);
      }
    } catch (error) {
      console.error("Failed to fetch available matches:", error);
      setAvailableMatches([]);
    }
  }, []);

  const fetchAssignedMatches = useCallback(async (gameweekId) => {
    if (!gameweekId) return;

    try {
      const response = await get(`/admin/gameweek/${gameweekId}/matches`);

      if (response?.data?.matches) {
        const mapped = response.data.matches.map((m) => ({
          id: m.id,
          optaFixtureId: m.optaFixtureId,
          kickoffAt: m.kickoffAt,
          gameweek: m.gameweek,
          status: m.status,
          homeScore: m.homeScore,
          awayScore: m.awayScore,
          homeContestantId: m.homeContestantId,
          awayContestantId: m.awayContestantId,
          homeContestantName: m.homeContestantName || 'TBD',
          awayContestantName: m.awayContestantName || 'TBD',
          tournamentName: m.tournamentName || null,
          sourceTournamentCalendarId: m.sourceTournamentCalendarId || null,
        }));

        setAssignedMatches(mapped);
      } else {
        setAssignedMatches([]);
      }
    } catch (error) {
      console.error("Failed to fetch assigned matches:", error);
      setAssignedMatches([]);
    }
  }, []);

  const fetchTournament = useCallback(async () => {
    try {
      const response = await get(`/admin/tournament-calendar/${tournamentId}`);
      if (response?.data) {
        setTournament(response.data);
      } else {
        setTournament(null);
      }
    } catch (error) {
      console.error("Failed to fetch tournament:", error);
      setTournament(null);
    }
  }, [tournamentId]);

  const isWorldCup =
    tournament?.competition_name?.toLowerCase().includes("world cup") ||
    tournament?.competition_name?.toLowerCase().includes("fifa world");

  const loadData = useCallback(async () => {
    setLoading(true);

    await fetchTournament();

    const allGameweeks = await fetchGameweeks();

    const targetGameweekId = urlGameweekId || selectedGameweekId;
    if (targetGameweekId) {
      const gw = allGameweeks.find((g) => g.id === targetGameweekId);
      if (gw) {
        setSelectedGameweek(gw);
        setSelectedGameweekId(gw.id);
        await Promise.all([
          fetchAssignedMatches(gw.id),
          fetchAvailableMatches(gw.id),
        ]);
      }
    }

    setLoading(false);
  }, [
    fetchTournament,
    fetchGameweeks,
    fetchAvailableMatches,
    fetchAssignedMatches,
    urlGameweekId,
    selectedGameweekId,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedGameweekId && gameweeks.length > 0) {
      const gw = gameweeks.find((g) => g.id === selectedGameweekId);
      if (gw) {
        setSelectedGameweek(gw);
      }
    }
  }, [gameweeks, selectedGameweekId]);

  const handleGameweekChange = async (event) => {
    const newGameweekId = event.target.value;
    setSelectedGameweekId(newGameweekId);

    if (newGameweekId) {
      const gw = gameweeks.find((g) => g.id === newGameweekId);
      setSelectedGameweek(gw);
      await Promise.all([
        fetchAssignedMatches(newGameweekId),
        fetchAvailableMatches(newGameweekId),
      ]);

      setSearchParams({ gameweek: newGameweekId });
    } else {
      setSelectedGameweek(null);
      setAssignedMatches([]);
      setAvailableMatches([]);
      setSearchParams({});
    }
  };

  const filteredAvailableMatches = useMemo(() => {
    return availableMatches.filter((match) => {
      if (dateFilter && match.kickoffAt) {
        const matchDate = new Date(match.kickoffAt).toISOString().split("T")[0];
        if (matchDate !== dateFilter) return false;
      }

      if (teamFilter) {
        const teamLower = teamFilter.toLowerCase();
        if (
          !match.homeContestantName.toLowerCase().includes(teamLower) &&
          !match.awayContestantName.toLowerCase().includes(teamLower)
        ) {
          return false;
        }
      }

      if (statusFilter && match.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [availableMatches, dateFilter, teamFilter, statusFilter]);

  const handleAssignMatches = async (matchIds) => {
    if (!selectedGameweekId) {
      setSnackbar({
        open: true,
        message: "Please select a gameweek first",
        severity: "warning",
      });
      return;
    }

    try {
      setSaving(true);

      const response = await post(
        `/admin/gameweek/${selectedGameweekId}/matches/bulk`,
        {
        matchIds: matchIds,
        }
      );

      if (response?.success) {
        await Promise.all([
          fetchGameweeks(),
          fetchAssignedMatches(selectedGameweekId),
          fetchAvailableMatches(selectedGameweekId),
        ]);

        setSnackbar({
          open: true,
          message: `${matchIds.length} match(es) assigned successfully`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Failed to assign matches:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to assign matches",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMatches = async (matchIds) => {
    try {
      setSaving(true);

      const response = await delWithBody(
        `/admin/gameweek/${selectedGameweekId}/matches/bulk/remove`,
        {
          matchIds: matchIds,
        }
      );

      if (response?.success) {
        await Promise.all([
          fetchGameweeks(),
          fetchAssignedMatches(selectedGameweekId),
          fetchAvailableMatches(selectedGameweekId),
        ]);

        setSnackbar({
          open: true,
          message: `${matchIds.length} match(es) removed successfully`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Failed to remove matches:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to remove matches",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    setSnackbar({
      open: true,
      message: `All changes saved for ${selectedGameweek?.name}!`,
      severity: "success",
    });
  };

  const handleBack = () => {
    navigate(`/dashboard/tournaments/${tournamentId}/gameweeks`);
  };

  const clearFilters = () => {
    setDateFilter("");
    setTeamFilter("");
    setStatusFilter("");
  };

  const allTeams = useMemo(() => {
    const teams = new Set();
    availableMatches.forEach((m) => {
      teams.add(m.homeContestantName);
      teams.add(m.awayContestantName);
    });
    return Array.from(teams).sort();
  }, [availableMatches]);

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 4 }}>
          Tournament not found
        </Alert>
      </Container>
    );
  }

  if (isWorldCup) {
    return (
      <Container maxWidth="xl">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Gameweek builder is only available for non-World Cup tournaments.
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/dashboard/tournaments/${tournamentId}`)}
          sx={{ mt: 2 }}
        >
          Back to Tournament
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gameweek Builder | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Typography
            component={RouterLink}
            to="/dashboard/tournaments"
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Tournaments
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${tournamentId}`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {tournament?.competition_name || "Tournament"}
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${tournamentId}/gameweeks`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Gameweeks
          </Typography>
          <Typography color="text.primary">Gameweek Builder</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4">Gameweek Builder</Typography>
            {selectedGameweek && (
              <>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {selectedGameweek.name} • {assignedMatches.length} matches
                </Typography>
                {selectedGameweek.startsAt && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {format(new Date(selectedGameweek.startsAt), "MMM dd, HH:mm")}
                    {" — "}
                    {format(new Date(selectedGameweek.endsAt), "MMM dd, HH:mm")}
                    {" • Deadline: "}
                    {format(new Date(selectedGameweek.deadlineAt), "MMM dd, HH:mm")}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !selectedGameweekId}
            >
              {saving ? <CircularProgress size={20} /> : "Save Changes"}
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Select Gameweek</InputLabel>
            <Select
              value={selectedGameweekId}
              onChange={handleGameweekChange}
              label="Select Gameweek"
            >
              <MenuItem value="">
                <em>-- Select a gameweek to assign matches --</em>
              </MenuItem>
              {gameweeks.map((gw) => (
                <MenuItem key={gw.id} value={gw.id}>
                  Gameweek {gw.number}: {gw.name}
                  <Typography
                    component="span"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    ({gw.assignedMatches} matches)
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!selectedGameweekId && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Please select a gameweek from the dropdown above to start
              assigning matches.
            </Alert>
          )}
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <FilterIcon color="action" />
            <Typography variant="h6">Filters</Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Team"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">All Teams</MenuItem>
                {allTeams.map((team) => (
                  <MenuItem key={team} value={team}>
                    {team}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="fixture">Upcoming</MenuItem>
                <MenuItem value="playing">Live</MenuItem>
                <MenuItem value="played">Played</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="postponed">Postponed</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                fullWidth
                disabled={!dateFilter && !teamFilter && !statusFilter}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>

          {(dateFilter || teamFilter || statusFilter) && (
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Active filters:
              </Typography>
              {dateFilter && (
                <Chip
                  size="small"
                  label={`Date: ${dateFilter}`}
                  onDelete={() => setDateFilter("")}
                />
              )}
              {teamFilter && (
                <Chip
                  size="small"
                  label={`Team: ${teamFilter}`}
                  onDelete={() => setTeamFilter("")}
                />
              )}
              {statusFilter && (
                <Chip
                  size="small"
                  label={`Status: ${statusFilter}`}
                  onDelete={() => setStatusFilter("")}
                />
              )}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          {selectedGameweekId ? (
            <GameweekAssignmentPanel
              availableMatches={filteredAvailableMatches}
              assignedMatches={assignedMatches}
              onAssignMatches={handleAssignMatches}
              onRemoveMatches={handleRemoveMatches}
              isCompact={false}
            />
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Gameweek Selected
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please select a gameweek from the dropdown above to view and
                assign matches.
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default GameweekBuilderPage;
